import _get from 'lodash/get';
import assert from 'http-assert-plus';
import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import * as Sentry from '@sentry/browser';

const localStorage = 'localStorage' in global && global.localStorage ? global.localStorage : null;
const sessionStorage = 'sessionStorage' in global && global.sessionStorage ? global.sessionStorage : null;

export function createApolloClient({ apiURL, apiHeaders }) {
  if (!apiURL) {
    throw new TypeError('Missing apiURL from createApolloClient');
  }

  const apolloCache = new InMemoryCache();
  const apolloLinks = [];

  if (process.env.VUE_ENV === 'server') {
    if (apiHeaders) {
      apolloLinks.push(new ApolloLink((operation, next) => {
        operation.setContext({
          headers: apiHeaders,
        });

        return next(operation);
      }));
    }
  } else if (process.env.VUE_ENV === 'client') {
    if (process.env.ADMIN_UI) {
      apolloLinks.push(new ApolloLink((operation, next) => {
        const hostname = sessionStorage ? JSON.parse(sessionStorage.getItem('hostname')) : null;
        const token = sessionStorage ? JSON.parse(sessionStorage.getItem('token')) : null;
        assert(hostname && typeof hostname === 'string', 'Missing admin hostname');
        assert(token && typeof token === 'string', 'Missing admin token');

        operation.setContext({
          uri: `https://${hostname}/api`,
          headers: {
            Authorization: `user ${token}`,
          },
        });

        return next(operation);
      }));
    } else {
      apolloLinks.push(new ApolloLink((operation, next) => {
        const headers = {};

        const token = localStorage ? JSON.parse(localStorage.getItem('token')) : null;
        if (typeof token === 'string' && token.length) {
          headers.Authorization = (token.startsWith('admin ') || token.startsWith('user ')) ? token : `user ${token}`;
        }

        if (process.env.NODE_ENV === 'development') {
          headers['X-RL-Debug'] = 1;
        }

        operation.setContext({ headers });
        return next(operation);
      }));
    }

    apolloLinks.push(onError(({ operation, graphQLErrors, networkError }) => {
      const { operationName, variables } = operation;

      if (networkError) {
        if (networkError.statusCode === 403) {
          localStorage.removeItem('token');
        }

        if (operationName !== 'resumeSession') {
          Sentry.withScope(scope => {
            scope.setTag('queryName', operationName);
            scope.setExtra('queryVariables', JSON.stringify(variables));
            Sentry.captureException(networkError);
          });
        }

        Vue.$log.error('networkError', networkError);
      }

      if (graphQLErrors) {
        graphQLErrors.forEach(err => {
          Sentry.addBreadcrumb({
            category: 'graphql',
            level: 'error',
            message: err.message,
            data: {
              queryName: operationName,
              queryVariables: JSON.stringify(variables),
            },
          });

          Vue.$log.error('graphQLError', err);
        });
      }
    }));

    // If there's an initial Apollo state from the server, fill it
    if (window.__APOLLO_STATE__) {
      apolloCache.restore(window.__APOLLO_STATE__.defaultClient);
    }
  }

  apolloLinks.push(createHttpLink({ uri: apiURL, fetch: customFetch }));

  const apolloClient = new ApolloClient({
    link: ApolloLink.from(apolloLinks),
    cache: apolloCache,
    ...(process.env.VUE_ENV === 'server' ? {
      // Set this on the server to optimize queries when SSR
      ssrMode: true,
    } : {
      // This will temporary disable query force-fetching
      ssrForceFetchDelay: 100,
    }),
  });

  Vue.mixin({
    methods: {
      $findApolloError(errors, key, value) {
        return (Array.isArray(errors) ? errors : [ errors ]).find(err => _get(err, key) === value) || null;
      },
    },
  });

  return new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        // error(err) {
        //   this.$notifyFatalErr(err);
        // },
        // errorPolicy: 'all',
        // fetchPolicy: 'network-only',
        prefetch: false,
      },
    },
    errorHandler(err) {
      Vue.$log.error('Apollo errorHandler', err);
      Sentry.captureException(err);
    },
  });
}

async function customFetch(uri, opts) {
  const res = await fetch(uri, opts);
  assert(res.status <= 500, res.status, `${res.status} ${res.statusText}`);
  return res;
}



// WEBPACK FOOTER //
// ./src/apollo.js